import React, { useEffect, useState } from "react";
import "./teamManagement.css";
import { useDispatch } from "react-redux";
import API_Root from "../../Constants/API_Root";
import { toggleToast } from "../../Actions/ToggleToast";
import { authFetch } from "../../Authentication/authService";

const ReportsVault = ({ selectedTeamId }) => {
  const dispatch = useDispatch();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetails, setReportDetails] = useState("");
  const [loadingDetails, setLoadingDetails] = useState(false);

  // =========================
  // FETCH REPORTS
  // =========================
  const fetchReports = async () => {
    if (!selectedTeamId) return;

    setLoading(true);
    try {
      const res = await authFetch(`${API_Root}files/fetch-all-reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team_id: selectedTeamId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      setReports(data?.files || []);
    } catch (err) {
      dispatch(
        toggleToast({ show: true, type: "error", message: err.message })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [selectedTeamId]);

  // =========================
  // FETCH DETAILS
  // =========================
  const fetchReportDetails = async (fileName) => {
    setLoadingDetails(true);
    setSelectedReport(fileName);

    try {
      const res = await authFetch(`${API_Root}files/get-file-contents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_name: fileName }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      setReportDetails(data.content || "");
    } catch (err) {
      dispatch(
        toggleToast({ show: true, type: "error", message: err.message })
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedReport(null);
    setReportDetails("");
  };

  const handleDownloadReport = async (reportName) => {
    try {
      const res = await authFetch(`${API_Root}files/download-any-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          report_name: reportName,
        }),
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error?.message || "Failed to download report");
      }

      const blob = await res.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = reportName;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "Report downloaded successfully",
        })
      );
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
    }
  };

  // =========================
  // 🧠 PARSER
  // =========================
  const parseReport = (text) => {
    if (!text) return null;

    const lines = text.split("\n").map((l) => l.trim());

    const getValue = (label) =>
      lines
        .find((l) => l.startsWith(label))
        ?.replace(label, "")
        .trim();

    const summary = {
      aligned: getValue("Aligned:"),
      partial: getValue("Partially Aligned:"),
      notAligned: getValue("Not Aligned:"),
      insufficient: getValue("Insufficient Info:"),
    };

    const tableStart = lines.findIndex((l) => l.includes("# | ESAP Action"));

    let rows = [];

    if (tableStart !== -1) {
      rows = lines
        .slice(tableStart + 1)
        .filter((l) => l.includes("|"))
        .map((row) => {
          const cols = row.split("|").map((c) => c.trim());
          return {
            id: cols[0],
            action: cols[1],
            status: cols[2],
            justification: cols[3],
          };
        });
    }

    return {
      generatedFor: getValue("Generated for:"),
      date: getValue("Date:"),
      summary,
      rows,
    };
  };

  const parsed = parseReport(reportDetails);

  return (
    <div className="tm-wrapper">
      {/* ========================= */}
      {/* REPORT LIST */}
      {/* ========================= */}
      <div className="tm-card">
        <div className="tm-card-header">
          <h4>Reports Vault</h4>
          <span>{reports.length} reports</span>
        </div>

        {loading ? (
          <p className="empty">Loading reports...</p>
        ) : reports.length === 0 ? (
          <p className="empty">No reports found</p>
        ) : (
          <table className="tm-table">
            <thead>
              <tr>
                <th>Document Title</th>
                <th>Date Created</th>
                <th>Initiated By</th>
                <th>KB Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {reports.map((report, index) => (
                <tr
                  key={index}
                  className="clickable-row"
                  // onClick={() => fetchReportDetails(report.file_name)}
                >
                  <td className="link">
                    <a
                      href={report.download_url || "#"}
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {report.file_name}
                    </a>
                  </td>

                  <td>
                    {report.created_at
                      ? new Date(report.created_at).toLocaleDateString()
                      : "N/A"}
                  </td>

                  <td>{report.initiated_by || "N/A"}</td>

                  <td>
                    <span
                      className={report.kb_status ? "badge success" : "badge"}
                    >
                      {report.kb_status ? "Yes" : "No"}
                    </span>
                  </td>

                  <td>
                    <td>
                      <button
                        className="tm-download-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadReport(report.file_name);
                        }}
                      >
                        Download
                      </button>
                    </td>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ========================= */}
      {/* DETAILS PANEL */}
      {/* ========================= */}
      {/* {selectedReport && (
        <div className="tm-card">
          <div className="tm-card-header">
            <h4>{selectedReport}</h4>
            <button onClick={handleCloseDetails}>Close</button>
          </div>

          {loadingDetails ? (
            <p className="empty">Loading details...</p>
          ) : !parsed ? (
            <pre className="tm-raw">{reportDetails}</pre>
          ) : (
            <>
              <div className="tm-report-header">
                <p><strong>Generated For:</strong> {parsed.generatedFor}</p>
                <p><strong>Date:</strong> {parsed.date}</p>
              </div>
              <div className="tm-summary-grid">
                <div className="tm-summary-card">Aligned: {parsed.summary.aligned}</div>
                <div className="tm-summary-card">Partial: {parsed.summary.partial}</div>
                <div className="tm-summary-card">Not Aligned: {parsed.summary.notAligned}</div>
                <div className="tm-summary-card">Insufficient: {parsed.summary.insufficient}</div>
              </div>
              <div className="tm-table-wrapper">
                <table className="tm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>ESAP Action</th>
                      <th>Status</th>
                      <th>Justification</th>
                    </tr>
                  </thead>

                  <tbody>
                    {parsed.rows.map((row, i) => (
                      <tr key={i}>
                        <td>{row.id}</td>
                        <td>{row.action}</td>
                        <td>{row.status}</td>
                        <td>{row.justification}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )} */}
    </div>
  );
};

export default ReportsVault;
